import React from 'react'
import Link from 'next/link'
import { Breadcrumb } from '../commons/Breadcrumb'
import { MarketHead } from './MarketHead'
import { MarketBooking } from './MarketBooking'
import { ServicingPanel } from './AirconPanels'
import { InfoCards, IntroStrip, MarketSection, Prose } from './ui'
import { MarketAirconHero } from './MarketAirconHero'
import { MarketDealsSection } from './AirconSections'
import { WhyUs } from '../commons/service-hub/WhyUs'
import { AirconServices } from '../index/service-category/aircon-service/AirconServices'
import { AirconServiceMatcher } from '../index/service-category/aircon-service/AirconServiceMatcher'
import { AcServiceLocality } from '../index/service-category/aircon-service/AcServiceLocality'
import { buildContext } from './content'
import { getAirconSections } from './sections'
import { FAQ_COUNTS, airconFaqBank, mergeFaqs } from './faqs'
import { Faq } from '../commons/Faq'
import { breadcrumbNode, faqNode, graph, serviceNode, webPageNode } from '../../utils/market-schema'
import { capitalise, getLocality, t } from '../../utils/markets'

/**
 * /<cc>/<aircon hub>/locality/<slug>. `copy` is that locality's entry from
 * data/markets/<cc>/aircon/<city>/localities.js:
 *   { paragraphs: [..], homes: '..', watch: '..', defaultMount: 'Wall'|'Ducted'|'Ceiling' }
 */
export const AirconLocalityPage = ({ countryCd, citySlug, localitySlug, tables, requestCategorySettings, modifiedDate, copy }) => {
    const ctx = buildContext({ countryCd, citySlug, tables })
    const { market, city, money } = ctx
    const sections = getAirconSections(ctx)
    const locality = getLocality(countryCd, citySlug, localitySlug)
    const hubPath = market.hubs.aircon[citySlug]
    const path = `${hubPath}/locality/${localitySlug}`
    const category = t(market, 'acCategory')
    const placeName = locality.state ? `${locality.name}, ${locality.state}` : `${locality.name}, ${city.name}`

    const h1 = `${category} in ${locality.name}`
    const faqs = mergeFaqs(FAQ_COUNTS.locality, airconFaqBank(ctx).locality(locality.name))
    const title = `${category} ${locality.name} from ${money(tables.general[0].wall)} | StringsSG`
    const description = `${category} in ${placeName}: ${t(market, 'acGeneral').toLowerCase()} from ${money(tables.general[0].wall)}, ${t(market, 'acChemicalWash').toLowerCase()} from ${money(tables.chemicalWash[0].wall)}, gas and repairs. Upfront prices.`

    const nearby = city.localities
        .filter((l) => l.slug !== localitySlug)
        .map((l) => ({ ...l, d: Math.hypot(l.lat - locality.lat, l.lng - locality.lng) }))
        .sort((a, b) => a.d - b.d)
        .slice(0, 8)
        .map((l) => ({ href: `${hubPath}/locality/${l.slug}`, label: l.name }))

    const jsonLd = graph(
        webPageNode({ market, path, title, description, dateModified: modifiedDate }),
        breadcrumbNode(path, h1),
        {
            ...serviceNode({
                market,
                city,
                path,
                name: h1,
                serviceType: 'Air conditioner servicing',
                description,
                offers: [
                    { name: `${t(market, 'acGeneral')}, 1 unit`, price: tables.general[0].wall },
                    { name: `${t(market, 'acChemicalWash')}, 1 unit`, price: tables.chemicalWash[0].wall },
                ],
            }),
            areaServed: { '@type': 'Place', name: placeName, geo: { '@type': 'GeoCoordinates', latitude: locality.lat, longitude: locality.lng } },
        },
        faqs.length ? faqNode(path, faqs) : null,
    )

    return (
        <>
            <MarketHead market={market} path={path} title={title} description={description} jsonLd={jsonLd} />
            <MarketBooking market={market} city={city} requestType="Aircon Services" requestCategorySettings={requestCategorySettings}>
                <MarketAirconHero
                    market={market}
                    tables={tables}
                    panel={{ type: 'servicing', defaultMount: 'Wall' }}
                    title={h1}
                    subtitle={`Upfront prices in ${locality.name}, travel included`}
                    image="blog/living/repairman-doing-air-conditioner-service.jpg"
                    imageAlt={`${t(market, 'acCategory')} technician at work in ${locality.name}`}
                    pills={['Same price across ' + city.name, 'Travel included']}
                />
                <Breadcrumb pageTitle={h1} jsonLd={false} />
                <IntroStrip
                    eyebrow={`${city.name} · ${locality.name}`}
                    lead={copy.paragraphs[0]}
                    facts={[`${t(market, 'acGeneral')} from ${money(tables.general[0].wall)}`, 'Upfront prices', 'Travel included']}
                />

                <MarketSection id="prices" title={`Prices in ${locality.name}`} intro={`Same prices across ${city.name} - no area surcharge.`} tone="muted">
                    <ServicingPanel market={market} tables={tables} defaultMount={copy.defaultMount} />
                </MarketSection>

                <MarketSection title={`${capitalise(t(market, 'acShort'))} notes for ${locality.name}`} narrow>
                    <Prose paragraphs={copy.paragraphs.slice(1)} />
                    <div className="mt-6">
                        <InfoCards
                            columns={2}
                            items={[
                                { title: 'Homes here', body: copy.homes },
                                { title: 'What to watch for', body: copy.watch },
                            ]}
                        />
                    </div>
                </MarketSection>

                <MarketDealsSection title={`${category} Offers in ${locality.name}`} tables={tables} />
                <AirconServiceMatcher
                    rows={sections.matcherRows}
                    title={sections.matcher.title}
                    intro={sections.matcher.intro}
                    caption={sections.matcher.caption}
                    footnote={sections.matcher.footnote}
                />
                <WhyUs id="ac-why-us" theme="dark" title={sections.whyUs.title} subtitle={sections.whyUs.subtitle} reasons={sections.whyUs.reasons} />
                <AirconServices services={sections.serviceCards} title={sections.services.title} intro={sections.services.intro} />
                <AcServiceLocality links={nearby} title={`${category} Near ${locality.name}`} />
                <section className="max-w-4xl mx-auto px-4 py-10" aria-label="Frequently asked questions">
                    <Faq questionsAnswers={faqs} title={`${category} in ${locality.name}: Questions`} showSearch={false} jsonLd={false} />
                </section>
                <p className="max-w-5xl mx-auto px-4 py-6 text-sm text-slate-600">
                    All areas: <Link href={hubPath} className="font-semibold text-orange-600 hover:underline">{category} {city.name}</Link>
                </p>
                {/* Room for the sticky Book / WhatsApp bar on mobile */}
                <div aria-hidden="true" className="h-20 md:hidden" />
            </MarketBooking>
        </>
    )
}
